// OWNER: LG-ADMIN. §10 tab 4 (per search): funnel header (raw -> dropped by gate, each clickable -> held -> survivors
// -> A/B/C/D), the results table (tier, priority, name, where, top 3 reasons, primary hook with usage, flags, links),
// filters, review (Approve tiers, per-row approve / reject with a reason), Import to campaign (country match, draft or
// live) with the ImportReport, and Export CSV (the server rebuilds it from D1). A row opens BusinessDrawer read-only.
// A smoke or pilot search shows its report (§5.4 checks with values, §6.2 pilot numbers) above the funnel.
import { useEffect, useMemo, useState } from 'preact/hooks';
import type {
  DrawerData, DropGate, LeadFlag, LeadPlaceStatus, LeadResultsQuery, LeadRow, NicheProfile, SearchFunnel, SearchImportResponse, Tier,
} from '../../../src/shared/api-leadgen';
import { navigate } from '../app';
import { Empty, ErrorBanner, Modal, toast } from '../components/ui';
import { ApiErrorBox, Check, ConfirmModal, Field, FixedHead, inputValue, useLoad } from '../admin/ui';
import { apiErrorView, type ApiErrorView } from '../admin/errors';
import { adminApi } from '../admin/endpoints';
import { formatPhone } from '../admin/phone';
import { BusinessDrawer } from '../dialer/drawer/BusinessDrawer';
import { leadgenApi } from './endpoints';
import {
  DROP_GATE_LABELS, FLAG_LABELS, csvQuery, OFFER_LABELS, PLACE_STATUS_TONES, STATUS_LABELS, STATUS_TONES, TIER_TONES, areasSummary, deepLinkPath,
  droppedTotal, funnelDrops, importableCampaigns, int, reasonText, rowReviewActions, searchStateOptions, usd2,
} from './logic';
import { RESULT_COLUMNS } from './layout';
import { Blockers, ExtLink, Money, ToneChip } from './parts';
import { SearchReportView } from './ReportPanel';
import { useMe } from '../me';

const PAGE_SIZE = 50;
const TIERS: Tier[] = ['A', 'B', 'C', 'D'];
const PLACE_STATUSES: LeadPlaceStatus[] = ['scored', 'approved', 'rejected', 'imported', 'held', 'dropped', 'survivor', 'raw'];

export function ResultsTab(props: { searchId: number | null }) {
  const id = props.searchId;
  return (
    <div class="stack" style="gap:12px">
      <SearchPicker current={id} />
      {id === null ? <Empty>Pick a search to review its leads.</Empty> : <Results key={id} searchId={id} />}
    </div>
  );
}

function SearchPicker(props: { current: number | null }) {
  const list = useLoad(() => leadgenApi.searches(50, 0), []);
  const items = list.data?.items ?? [];
  return (
    <div class="adm-toolbar">
      <Field label="Search" style="min-width:420px">
        <select value={props.current ?? ''} onChange={(e) => { const v = inputValue(e); navigate(deepLinkPath('results', v ? Number(v) : null)); }}>
          <option value="">{list.loading && !list.data ? 'Loading…' : 'Pick a search…'}</option>
          {props.current !== null && !items.some((s) => s.id === props.current) ? <option value={props.current}>#{props.current}</option> : null}
          {items.map((s) => <option value={s.id}>#{s.id} · {s.nicheKey} v{s.nicheVersion} · {areasSummary(s.areas)} · {STATUS_LABELS[s.status]}</option>)}
        </select>
      </Field>
      {list.error ? <span class="lg-err-line">{list.error}</span> : null}
    </div>
  );
}

function Results(props: { searchId: number }) {
  const sid = props.searchId;
  // SPEC §19: setters read their own searches' results; review, import and the CSV export stay with admins.
  const canReview = useMe().role === 'admin';
  const detail = useLoad(() => leadgenApi.search(sid), [sid]);
  const niches = useLoad(() => leadgenApi.niches(), []);
  const [q, setQ] = useState<LeadResultsQuery>({ page: 1, pageSize: PAGE_SIZE });
  const results = useLoad(() => leadgenApi.results(sid, q), [sid, JSON.stringify(q)]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [drawer, setDrawer] = useState<{ row: LeadRow; data: DrawerData | null; error: string | null } | null>(null);
  const [rejecting, setRejecting] = useState<LeadRow[] | null>(null);
  const [approvingTiers, setApprovingTiers] = useState<Tier[] | null>(null);
  const [tierPick, setTierPick] = useState<Tier[]>(['A', 'B']);

  const d = detail.data;
  const search = d?.search ?? null;
  const geo = useLoad(async () => (search ? leadgenApi.geo(search.country) : null), [search?.country]);
  const stateOptions = useMemo(() => (search ? searchStateOptions(search.areas, geo.data) : []), [search, geo.data]);
  const funnel = d?.funnel ?? null;
  const profile = useMemo(() => (niches.data ?? []).find((n) => search && n.key === search.nicheKey && n.version === search.nicheVersion)?.profile ?? null, [niches.data, search]);
  const rows = results.data?.items ?? [];
  const total = results.data?.total ?? 0;
  const page = q.page ?? 1;
  const pages = Math.max(1, Math.ceil(total / (q.pageSize ?? PAGE_SIZE)));
  const setFilter = (patch: Partial<LeadResultsQuery>) => { setSelected(new Set()); setQ((cur) => ({ ...cur, ...patch, page: 1 })); };
  const refresh = async () => { setSelected(new Set()); await Promise.all([results.reload(), detail.reload()]); };

  const openDrawer = async (row: LeadRow) => {
    setDrawer({ row, data: null, error: null });
    try { const data = await leadgenApi.placeDrawer(row.id); setDrawer((cur) => (cur && cur.row.id === row.id ? { ...cur, data } : cur)); }
    catch (e) { setDrawer((cur) => (cur && cur.row.id === row.id ? { ...cur, error: apiErrorView(e).message } : cur)); }
  };
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawer(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer !== null]);

  const approveIds = async (ids: number[]) => {
    try {
      const r = await leadgenApi.review(sid, { approve: ids });
      toast(`Approved ${r.approved}${r.skipped ? `, skipped ${r.skipped} (not in a reviewable state)` : ''}.`, 'ok');
      await refresh();
    } catch (e) { toast(apiErrorView(e).message, 'bad'); }
  };

  const selRows = rows.filter((r) => selected.has(r.id));
  const toggle = (id: number, on: boolean) => setSelected((cur) => { const n = new Set(cur); if (on) n.add(id); else n.delete(id); return n; });
  const reviewable = rows.filter((r) => { const a = rowReviewActions(r.status); return a.approve || a.reject; });

  if (detail.error && !d) return <ErrorBanner error={detail.error} />;
  if (!d || !search || !funnel) return <div class="muted">Loading search #{sid}…</div>;

  return (
    <div class="stack" style="gap:12px">
      <div class="lg-card-head">
        <div>
          <h2 style="margin:0">#{search.id} · {search.nicheKey} v{search.nicheVersion} · {areasSummary(search.areas)}</h2>
          <div class="adm-sub">
            <ToneChip tone={STATUS_TONES[search.status]}>{STATUS_LABELS[search.status]}</ToneChip> · sorted for {OFFER_LABELS[search.offer]} · spent <Money usd={search.spentUsd} /> of {usd2(search.budgetUsd)}
          </div>
        </div>
        {canReview ? (
          <a class="btn btn-sm" href={leadgenApi.resultsCsvUrl(sid, csvQuery(q))} download={`lead-search-${sid}.csv`}
            title="Exports the rows that match the filters below. With no status filter: every kept lead (dropped, raw and held rows are left out).">
            Export CSV{q.status && results.data ? ` (${int(total)} rows)` : ''}
          </a>
        ) : null}
      </div>
      {d.redLines.length ? <Blockers items={d.redLines} /> : null}

      <SearchReportView search={search} weekdayCloseThreshold={profile?.hours_norm?.weekday_close_threshold ?? null} />

      <Funnel funnel={funnel} q={q} onFilter={setFilter} />

      <div class="adm-toolbar">
        <Field label="Tier"><select value={q.tier ?? ''} onChange={(e) => setFilter({ tier: (inputValue(e) || undefined) as Tier | undefined })}>
          <option value="">Any</option>{TIERS.map((t) => <option value={t}>{t}</option>)}</select></Field>
        <Field label="Status"><select value={q.status ?? ''} onChange={(e) => setFilter({ status: (inputValue(e) || undefined) as LeadPlaceStatus | undefined, drop: undefined })}>
          <option value="">Any</option>{PLACE_STATUSES.map((s) => <option value={s}>{s}</option>)}</select></Field>
        <Field label="State"><select value={q.state ?? ''} onChange={(e) => setFilter({ state: inputValue(e) || undefined })}>
          <option value="">Any</option>{stateOptions.map((o) => <option value={o.value}>{o.label}</option>)}</select></Field>
        <Field label="Flag"><select value={q.flag ?? ''} onChange={(e) => setFilter({ flag: (inputValue(e) || undefined) as LeadFlag | undefined })}>
          <option value="">Any</option>{(Object.keys(FLAG_LABELS) as LeadFlag[]).map((f) => <option value={f}>{FLAG_LABELS[f].label}</option>)}</select></Field>
        <Field label="Drop reason"><select value={q.drop ?? ''} onChange={(e) => { const v = (inputValue(e) || undefined) as DropGate | undefined; setFilter({ drop: v, status: v ? 'dropped' : q.status === 'dropped' ? undefined : q.status }); }}>
          <option value="">Any</option>{(Object.keys(DROP_GATE_LABELS) as DropGate[]).map((g) => <option value={g}>{DROP_GATE_LABELS[g]}</option>)}</select></Field>
        <Field label="Hook"><select value={q.hasHook === undefined ? '' : String(q.hasHook)} onChange={(e) => { const v = inputValue(e); setFilter({ hasHook: v === '' ? undefined : v === 'true' }); }}>
          <option value="">Any</option><option value="true">Has a hook</option><option value="false">No hook</option></select></Field>
        <span class="grow" />
        <span class="lg-note">{int(total)} rows</span>
      </div>

      {canReview ? <div class="lg-bulk">
        <span class="eyebrow">Approve tiers</span>
        {TIERS.map((t) => <Check checked={tierPick.includes(t)} onChange={(v) => setTierPick((cur) => (v ? [...cur, t] : cur.filter((x) => x !== t)).sort())}
          label={t} title={t === 'D' ? 'Tier D is held back unless you tick it.' : undefined} />)}
        <button class="btn btn-sm btn-ink" disabled={tierPick.length === 0} onClick={() => setApprovingTiers(tierPick)}>Approve {tierPick.join('+') || '…'}</button>
        <span class="grow" />
        <span class="lg-note">{selected.size} selected</span>
        <button class="btn btn-sm" disabled={!selRows.some((r) => rowReviewActions(r.status).approve)} onClick={() => void approveIds(selRows.filter((r) => rowReviewActions(r.status).approve).map((r) => r.id))}>Approve selected</button>
        <button class="btn btn-sm" disabled={!selRows.some((r) => rowReviewActions(r.status).reject)} onClick={() => setRejecting(selRows.filter((r) => rowReviewActions(r.status).reject))}>Reject selected…</button>
      </div> : <div class="lg-note">An admin reviews these leads and imports the good ones into a campaign.</div>}

      <ErrorBanner error={results.error} />
      {results.loading && !results.data ? <div class="muted">Loading rows…</div> : null}
      {results.data && rows.length === 0 ? <Empty>No rows match these filters.</Empty> : null}
      {rows.length ? (
        <div class="adm-table-wrap">
          <table class="adm-fixed">
            <FixedHead cols={RESULT_COLUMNS} />
            <tbody>
              <tr class="adm-dim"><td>
                <input type="checkbox" aria-label="Select every reviewable row on this page" checked={reviewable.length > 0 && reviewable.every((r) => selected.has(r.id))}
                  onChange={(e) => setSelected((e.target as HTMLInputElement).checked ? new Set(reviewable.map((r) => r.id)) : new Set())} />
              </td><td colSpan={RESULT_COLUMNS.length - 1} class="lg-note">Page {page} of {pages}. Click a row for the business drawer.</td></tr>
              {rows.map((r) => <ResultRow row={r} canReview={canReview} selected={selected.has(r.id)} onToggle={(on) => toggle(r.id, on)}
                onOpen={() => void openDrawer(r)} onApprove={() => void approveIds([r.id])} onReject={() => setRejecting([r])} />)}
            </tbody>
          </table>
        </div>
      ) : null}
      {pages > 1 ? (
        <div class="row">
          <button class="btn btn-sm" disabled={page <= 1} onClick={() => setQ((c) => ({ ...c, page: page - 1 }))}>← Previous</button>
          <span class="lg-note">Page {page} of {pages}</span>
          <button class="btn btn-sm" disabled={page >= pages} onClick={() => setQ((c) => ({ ...c, page: page + 1 }))}>Next →</button>
        </div>
      ) : null}

      {canReview ? <ImportCard searchId={sid} country={search.country} nicheKey={search.nicheKey} profile={profile} approved={funnel.approved} imported={funnel.imported} onImported={refresh} /> : null}

      {approvingTiers ? (
        <ConfirmModal title={`Approve tiers ${approvingTiers.join('+')}?`} confirmLabel="Approve"
          body={<>Every scored lead in tier {approvingTiers.join(', ')} ({int(approvingTiers.reduce((a, t) => a + (funnel.tiers[t] ?? 0), 0))} scored in total, including any already reviewed) becomes approved. Approved leads import on the next Import.</>}
          onConfirm={async () => {
            const r = await leadgenApi.review(sid, { approve: { tiers: approvingTiers } });
            toast(`Approved ${r.approved}${r.skipped ? `, skipped ${r.skipped}` : ''}.`, 'ok');
            await refresh();
          }} onClose={() => setApprovingTiers(null)} />
      ) : null}
      {rejecting ? (
        <ConfirmModal title={`Reject ${rejecting.length === 1 ? rejecting[0].name : `${rejecting.length} leads`}?`} danger confirmLabel="Reject"
          noteLabel="Reason" noteRequired notePlaceholder="e.g. franchise, wrong niche, closed" body={<>Rejected leads don't import. The reason is kept with the row.</>}
          onConfirm={async (reason) => {
            const r = await leadgenApi.review(sid, { reject: rejecting.map((x) => ({ id: x.id, reason })) });
            toast(`Rejected ${r.rejected}${r.skipped ? `, skipped ${r.skipped}` : ''}.`, 'ok');
            await refresh();
          }} onClose={() => setRejecting(null)} />
      ) : null}
      {drawer ? (
        <div class="lg-drawer-panel" role="complementary" aria-label={`Business drawer: ${drawer.row.name}`}>
          <div class="lg-drawer-head">
            <span class="eyebrow grow">Business drawer · read-only (no live check, no Verify)</span>
            <button class="btn btn-sm" onClick={() => setDrawer(null)} aria-label="Close">Esc</button>
          </div>
          <div class="lg-drawer-body">
            <BusinessDrawer data={drawer.data} live={null} loading={!drawer.data && !drawer.error} error={drawer.error} readOnly onClose={() => setDrawer(null)} />
            {drawer.error ? <ErrorBanner error={drawer.error} /> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Funnel(props: { funnel: SearchFunnel; q: LeadResultsQuery; onFilter: (p: Partial<LeadResultsQuery>) => void }) {
  const f = props.funnel;
  const drops = funnelDrops(f);
  const all = { tier: undefined, status: undefined, drop: undefined };
  const step = (label: string, n: number, patch: Partial<LeadResultsQuery> | null, active: boolean) => patch
    ? <button type="button" class={`lg-step${active ? ' active' : ''}`} onClick={() => props.onFilter({ ...all, ...patch })}><b>{int(n)}</b><span>{label}</span></button>
    : <div class="lg-step"><b>{int(n)}</b><span>{label}</span></div>;
  const q = props.q;
  return (
    <div class="card stack" style="gap:8px">
      <div class="lg-funnel">
        {step('raw', f.raw, {}, !q.tier && !q.status && !q.drop)}
        <span class="lg-arrow">→</span>
        {step('dropped', droppedTotal(f), { status: 'dropped' }, q.status === 'dropped' && !q.drop)}
        <span class="lg-arrow">→</span>
        {step('held', f.held, { status: 'held' }, q.status === 'held')}
        <span class="lg-arrow">→</span>
        {step('survivors', f.survivors, null, false)}
        <span class="lg-arrow">→</span>
        {TIERS.map((t) => step(`tier ${t}`, f.tiers[t] ?? 0, { tier: t }, q.tier === t))}
        <span class="lg-arrow">·</span>
        {step('approved', f.approved, { status: 'approved' }, q.status === 'approved')}
        {step('imported', f.imported, { status: 'imported' }, q.status === 'imported')}
      </div>
      {drops.length ? (
        <div class="lg-drops" aria-label="Dropped by gate">
          {drops.map((d) => (
            <button type="button" class={`chip chip-bad${q.drop === d.gate ? ' active' : ''}`} title={`Show the ${d.count} rows dropped by ${d.gate}`}
              onClick={() => props.onFilter({ tier: undefined, status: 'dropped', drop: d.gate as DropGate })}>{d.label}: {int(d.count)}</button>
          ))}
        </div>
      ) : null}
      {Object.keys(f.failedTasks ?? {}).length ? (
        <div class="lg-note">Failed tasks: {Object.entries(f.failedTasks).map(([k, n]) => `${k} ${n}`).join(' · ')}. Signals from failed tasks show as “not measured”.</div>
      ) : null}
    </div>
  );
}

function ResultRow(props: { row: LeadRow; canReview: boolean; selected: boolean; onToggle: (on: boolean) => void; onOpen: () => void; onApprove: () => void; onReject: () => void }) {
  const r = props.row;
  const acts = props.canReview ? rowReviewActions(r.status) : { approve: false, reject: false };
  return (
    <tr class="adm-clickable" onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button,input,label')) props.onOpen(); }}>
      <td>{acts.approve || acts.reject ? <input type="checkbox" checked={props.selected} aria-label={`Select ${r.name}`} onChange={(e) => props.onToggle((e.target as HTMLInputElement).checked)} /> : null}</td>
      <td>{r.tier ? <ToneChip tone={TIER_TONES[r.tier]}>{r.tier}</ToneChip> : <span class="muted">—</span>}<span class="adm-cell-sub">{r.priority ?? '—'}</span></td>
      <td>
        <b>{r.name}</b>
        <span class="adm-cell-sub">{r.categoryName ?? 'no category'}</span>
        <ToneChip tone={PLACE_STATUS_TONES[r.status]}>{r.status}</ToneChip>
        {r.existingBusinessId ? <span class="adm-cell-sub" title="Matched an existing business; import adds a membership.">already a business</span> : null}
        {r.note ? <span class="adm-cell-sub" title={r.note}>{r.note}</span> : null}
      </td>
      <td>{[r.city, r.stateProv].filter(Boolean).join(', ') || '—'}</td>
      <td>
        {r.dropGate ? (
          <span class="lg-err-line">{DROP_GATE_LABELS[r.dropGate] ?? r.dropGate}{r.dropDetail ? <span class="adm-cell-sub" style="white-space:normal">{r.dropDetail}</span> : null}</span>
        ) : r.topReasons.length ? (
          <ul class="lg-reasons">{r.topReasons.slice(0, 3).map((x) => <li>{reasonText(x)}</li>)}</ul>
        ) : <span class="muted">{r.tier ? 'no scoring reasons' : 'not scored'}</span>}
      </td>
      <td>
        {r.hook ? (
          <div class="stack" style="gap:3px">
            <span class="lg-hook" title={r.hook.sayText}>{r.hook.sayText}</span>
            <span class="row" style="gap:4px;flex-wrap:wrap">
              <ToneChip tone={r.hook.usage === 'opener' ? 'ok' : r.hook.usage === 'mid_call' ? 'info' : ''}>{r.hook.usage.replace('_', '-')}</ToneChip>
              <ExtLink href={r.hook.evidenceUrl}>evidence</ExtLink>
            </span>
          </div>
        ) : <span class="muted">no hook</span>}
      </td>
      <td><div class="lg-chips">{r.flags.map((f) => { const l = FLAG_LABELS[f]; return <ToneChip tone={l?.tone ?? ''} title={l?.title}>{l?.label ?? f}</ToneChip>; })}</div></td>
      <td>
        <div class="lg-links">
          <ExtLink href={r.mapsUrl} title="Google Maps (the row's source)">Maps</ExtLink>
          {r.website ? <ExtLink href={r.website}>Site</ExtLink> : null}
          {r.phoneE164 ? <span class="lg-mono lg-nowrap" title={r.phoneE164}>{r.phoneDisplay ?? formatPhone(r.phoneE164)}</span> : null}
        </div>
        {acts.approve || acts.reject ? (
          <div class="lg-row-actions">
            {acts.approve ? <button class="btn btn-sm" onClick={props.onApprove}>Approve</button> : null}
            {acts.reject ? <button class="btn btn-sm" onClick={props.onReject}>Reject</button> : null}
          </div>
        ) : null}
      </td>
    </tr>
  );
}

function ImportCard(props: {
  searchId: number; country: 'US' | 'CA'; nicheKey: string; profile: NicheProfile | null;
  approved: number; imported: number; onImported: () => Promise<void>;
}) {
  const campaigns = useLoad(() => adminApi.campaigns(), []);
  const choices = useMemo(() => importableCampaigns(campaigns.data ?? [], { country: props.country, nicheKey: props.nicheKey }, props.profile), [campaigns.data, props.country, props.nicheKey, props.profile]);
  const [cid, setCid] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiErrorView | null>(null);
  const [report, setReport] = useState<SearchImportResponse | null>(null);
  const pick = choices.find((c) => c.campaign.id === cid) ?? null;

  const go = async () => {
    if (!pick) return;
    setBusy(true); setError(null);
    try {
      const rep = await leadgenApi.importToCampaign(props.searchId, { campaignId: pick.campaign.id });
      setReport(rep); setConfirming(false);
      toast(`Imported ${rep.imported} into ${pick.campaign.name}.`, 'ok');
      await props.onImported();
    } catch (e) { setError(apiErrorView(e)); setConfirming(false); } finally { setBusy(false); }
  };

  return (
    <div class="card stack">
      <div class="lg-card-head"><h3>Import to a campaign</h3><span class="lg-note">{int(props.approved)} approved · {int(props.imported)} imported so far. Only approved rows import.</span></div>
      <ErrorBanner error={campaigns.error} />
      {campaigns.data && choices.length === 0 ? <div class="adm-muted-box">No draft or live {props.country} campaign. Create one in Campaigns first.</div> : null}
      {choices.length ? (
        <div class="adm-toolbar">
          <Field label={`Campaign (${props.country}, draft or live)`} style="min-width:360px">
            <select value={cid ?? ''} onChange={(e) => setCid(inputValue(e) ? Number(inputValue(e)) : null)}>
              <option value="">Pick a campaign…</option>
              {choices.map((c) => <option value={c.campaign.id} disabled={!c.ok}>{c.campaign.name} · {c.campaign.niche} · {c.campaign.offer} · {c.campaign.status}{c.ok ? '' : ' (not allowed)'}</option>)}
            </select>
          </Field>
          <button class="btn btn-primary" disabled={!pick?.ok || busy || props.approved === 0} onClick={() => setConfirming(true)}>Import approved leads…</button>
        </div>
      ) : null}
      {pick && !pick.ok ? <div class="lg-err-line">{pick.reason}</div> : null}
      {pick?.ok && !pick.nicheMatches ? <div class="lg-note">This campaign's niche is “{pick.campaign.niche}”, not {props.nicheKey}.</div> : null}
      <ApiErrorBox error={error} />
      {report ? <ImportReportView report={report} /> : null}
      {confirming && pick ? (
        <Modal title="Import approved leads?" onClose={() => (busy ? null : setConfirming(false))}>
          <div class="stack">
            <div>Import the approved leads of search #{props.searchId} into <b>{pick.campaign.name}</b> ({pick.campaign.status})?</div>
            <div class="lg-note">Each row keeps its Google Maps source URL. Hooks import as unverified observations until a setter clicks Verify. Hand-pick states import at most the per-state cap.</div>
            <div class="row" style="justify-content:flex-end">
              <button class="btn" onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
              <button class="btn btn-primary" onClick={go} disabled={busy}>{busy ? 'Importing…' : 'Import'}</button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function ImportReportView(props: { report: SearchImportResponse }) {
  const r = props.report;
  const flagCounts = r.flagged.reduce<Record<string, number>>((m, f) => { m[f.flag] = (m[f.flag] ?? 0) + 1; return m; }, {});
  return (
    <div class="stack" style="gap:8px">
      <div class="lg-est-nums">
        <div class="adm-stat"><b>{int(r.total)}</b><span>rows sent</span></div>
        <div class="adm-stat"><b>{int(r.imported)}</b><span>new memberships</span></div>
        <div class="adm-stat"><b>{int(r.reusedBusinesses)}</b><span>existing businesses</span></div>
        <div class="adm-stat"><b>{int(r.rejected.length)}</b><span>rejected</span></div>
        <div class="adm-stat"><b>{int(r.lookupsQueued)}</b><span>line-type lookups queued</span></div>
      </div>
      {r.heldHandPick.length ? <div class="lg-note">Held by the hand-pick cap (still approved): {r.heldHandPick.map((h) => `${h.state} ${h.count}`).join(' · ')}</div> : null}
      {Object.keys(flagCounts).length ? <div class="lg-note">Flagged: {Object.entries(flagCounts).map(([k, n]) => `${k.replace(/_/g, ' ')} ${n}`).join(' · ')}</div> : null}
      {r.rejected.length ? (
        <div class="adm-table-wrap" style="max-height:260px">
          <table><thead><tr><th class="num">Row</th><th>Reason</th><th>Business</th></tr></thead>
            <tbody>{r.rejected.map((x) => <tr><td class="num">{x.row}</td><td>{x.reason}</td><td>{x.raw?.name ?? ''}</td></tr>)}</tbody></table>
        </div>
      ) : null}
    </div>
  );
}
